import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Drawer, Typography, IconButton, Tooltip, TextField, Chip,
  CircularProgress, Checkbox, Dialog, DialogContent, DialogActions, Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axiosInstance from './axiosInstance';
import KinAvatar from './KinAvatar';
import KinChart from './ui/KinChart';

const baseURL = process.env.REACT_APP_API_BASE_URL;
const PANEL_WIDTH = 400;

// Menschlich lesbare Werkzeug-Aktivität ("Kin liest gerade …")
const TOOL_LABELS = {
  leads_suchen: 'sucht Leads …',
  lead_lesen: 'liest einen Lead …',
  kontakte_suchen: 'sucht Kontakte …',
  projekte_suchen: 'sucht Projekte …',
  zeiten_lesen: 'liest Zeiteinträge …',
  teamkalender_lesen: 'schaut in den Teamkalender …',
  pipeline_konfiguration: 'liest die Pipeline-Konfiguration …',
  aenderung_vorschlagen: 'bereitet einen Vorschlag vor …',
  interaktion_vorschlagen: 'bereitet einen Eintrag vor …',
};

// Markdown in Kins Antworten — kompakt gestylt (Listen, Fettung, Tabellen)
const MD_SX = {
  '& p': { m: 0, mb: 0.75 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: 0.75, pl: 2.25 },
  '& ul:last-child, & ol:last-child': { mb: 0 },
  '& li': { mb: 0.25 },
  '& table': { borderCollapse: 'collapse', my: 0.75, width: '100%', fontSize: '0.8rem', display: 'block', overflowX: 'auto' },
  '& th': { textAlign: 'left', fontWeight: 650, fontSize: '0.7rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', px: 0.75, py: 0.4, whiteSpace: 'nowrap' },
  '& td': { borderBottom: '1px solid', borderColor: 'divider', px: 0.75, py: 0.4, verticalAlign: 'top' },
  '& tr:last-child td': { borderBottom: 'none' },
  '& a': { color: 'primary.main' },
  '& code': { fontFamily: 'monospace', fontSize: '0.8em', bgcolor: 'action.hover', px: 0.5, borderRadius: '4px' },
  '& h1, & h2, & h3, & h4': { fontSize: '0.9rem', fontWeight: 650, m: 0, mb: 0.5 },
  '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 1 },
};

const PROPOSAL_STATUS = {
  applied: { label: '✓ Übernommen', color: 'success.main' },
  dismissed: { label: 'Verworfen', color: 'text.disabled' },
  expired: { label: 'Veraltet — Daten haben sich geändert', color: 'warning.main' },
};

// Zeilen-Ergebnisse nach einer Massen-Änderung (A3)
const BULK_ROW_STATUS = {
  applied: { label: '✓', color: 'success.main', title: 'Übernommen' },
  expired: { label: '⟳', color: 'warning.main', title: 'Veraltet — zwischenzeitlich geändert' },
  fehler: { label: '✕', color: 'error.main', title: 'Fehlgeschlagen' },
  uebersprungen: { label: '–', color: 'text.disabled', title: 'Nicht ausgewählt' },
};

const SUGGESTIONS = [
  'Prüfe meine Zeiterfassung der letzten 30 Tage auf Lücken',
  'Welche Leads warten am längsten auf eine Antwort?',
  'Fasse den ältesten offenen Lead für mich zusammen',
];

// „Was kann Kin?" — kuratierte Beispiele je Thema. Vorlagen mit
// [Platzhalter] landen zum Ausfüllen im Eingabefeld statt direkt zu senden.
const HELP_SECTIONS = [
  {
    title: 'Leads',
    items: [
      'Welche Leads warten am längsten auf eine Antwort?',
      'Welche heißen Leads sind noch offen?',
      'Bei welchen offenen Leads fehlen Telefonnummer oder Firma?',
      'Fasse den Lead von [Name] zusammen — was wäre der nächste Schritt?',
    ],
  },
  {
    title: 'Zeiterfassung',
    items: [
      'Prüfe meine Zeiterfassung der letzten 30 Tage auf Lücken',
      'Wie viele Stunden habe ich diesen Monat auf welche Projekte gebucht?',
      'Habe ich doppelte oder auffällige Zeiteinträge im [Monat]?',
    ],
  },
  {
    title: 'Projekte & Team',
    items: [
      'Welche Projekte enden in den nächsten 30 Tagen?',
      'Wer ist nächste Woche abwesend?',
      'Bereite mich auf das Gespräch mit [Firma] vor',
    ],
  },
  {
    title: 'Eintragen & Korrigieren',
    items: [
      'Leg einen neuen Lead an: [Name], [Firma], [E-Mail]',
      'Leg meine Zeiten aus der angehängten Excel an (Datei über die Büroklammer anhängen)',
      'Korrigiere die Rechtschreibung meiner Zeiteinträge im [Monat]',
      'Bring alle Telefonnummern unserer Leads in ein einheitliches Format',
      'Trag bei Lead [Name] die Telefonnummer [Nummer] ein',
      'Mach aus dieser Notiz einen Chronik-Eintrag: [Notiz]',
    ],
  },
];

// Kommt mit der nächsten Ausbaustufe (A4) — bewusst nicht klickbar.
const HELP_UPCOMING = [
  'Lead-Daten per Websuche vervollständigen',
];

// Token ggf. auffrischen — gleicher Mechanismus wie im axios-Interceptor,
// hier nachgebaut, weil der Stream über fetch läuft (axios kann kein SSE).
async function ensureFreshToken() {
  let accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!accessToken) return null;
  try {
    const { exp } = jwtDecode(accessToken);
    if (exp < Date.now() / 1000 && refreshToken) {
      const r = await axios.post(`${baseURL}/token/refresh/`, { refresh: refreshToken });
      localStorage.setItem('access_token', r.data.access);
      accessToken = r.data.access;
    }
  } catch (e) {
    return null;
  }
  return accessToken;
}

// Diff-Karte eines Änderungsvorschlags (A2): Alt → Neu je Feld,
// Übernehmen/Verwerfen — der Status kommt nach der Aktion vom Server.
function ProposalCard({ item, onAction, disabled }) {
  const p = item.proposal;
  const done = PROPOSAL_STATUS[p.status];
  return (
    // flexShrink 0: im überlaufenden Flex-Verlauf dürfen Karten nie kollabieren
    <Box sx={{ ml: '30px', maxWidth: '92%', flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', fontSize: '0.83rem' }}>
      <Box sx={(theme) => ({ px: 1.5, py: 0.75, bgcolor: theme.palette.tokens.surface2, fontWeight: 650, fontSize: '0.76rem', color: 'text.secondary' })}>
        {p.kind === 'create' ? p.ziel : `Änderungsvorschlag · ${p.ziel}`}
      </Box>
      {p.aenderungen.map((c) => (
        <Box key={c.feld} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ flex: '0 0 92px', color: 'text.secondary', fontSize: '0.74rem', fontWeight: 550 }}>
            {c.feld}
          </Typography>
          <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {p.kind === 'update' && (
              <>
                <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>{c.alt || '—'}</Box>
                <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>→</Box>
              </>
            )}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{c.neu || '—'}</Box>
          </Box>
        </Box>
      ))}
      {p.begruendung && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider', fontStyle: 'italic' }}>
          {p.begruendung}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        {done ? (
          <>
            <Typography variant="caption" sx={{ color: done.color, fontWeight: 650 }}>{done.label}</Typography>
            {item.fehler && <Typography variant="caption" color="text.secondary">{item.fehler}</Typography>}
          </>
        ) : (
          <>
            <Chip label="Übernehmen" size="small" clickable={!disabled} disabled={disabled} color="primary"
              onClick={() => onAction(p.id, 'apply')} sx={{ fontWeight: 650 }} />
            <Chip label="Verwerfen" size="small" clickable={!disabled} disabled={disabled} variant="outlined"
              onClick={() => onAction(p.id, 'dismiss')} />
            {item.fehler && <Typography variant="caption" color="error.main">{item.fehler}</Typography>}
          </>
        )}
      </Box>
    </Box>
  );
}

// Sammelvorschlag (A3): Tabelle mit Zeilen-Auswahl. Übernehmen führt zur
// zweiten Bestätigung (Dialog) — Alexanders Regel für Massen-Anpassungen.
function BulkProposalCard({ item, onApply, onDismiss, disabled }) {
  const p = item.proposal;
  const resolved = p.status !== 'pending';
  const [selected, setSelected] = useState(() => new Set(p.aenderungen.map((_, i) => i)));
  const toggle = (i) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const allSelected = selected.size === p.aenderungen.length;

  return (
    // flexShrink 0: siehe ProposalCard — Karten nie zusammenquetschen
    <Box sx={{ ml: '30px', maxWidth: '92%', flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', fontSize: '0.83rem' }}>
      <Box sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: theme.palette.tokens.surface2 })}>
        {!resolved && (
          <Checkbox size="small" checked={allSelected} sx={{ p: 0.25 }}
            indeterminate={!allSelected && selected.size > 0} disabled={disabled}
            onChange={() => setSelected(allSelected ? new Set() : new Set(p.aenderungen.map((_, i) => i)))} />
        )}
        <Typography sx={{ fontWeight: 650, fontSize: '0.76rem', color: 'text.secondary', py: 0.25 }}>
          {p.ziel}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
        {p.aenderungen.map((row, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, px: 1.5, py: 0.6, borderTop: '1px solid', borderColor: 'divider' }}>
            {resolved ? (
              <Tooltip title={BULK_ROW_STATUS[row.status]?.title || ''}>
                <Typography component="span" sx={{ width: 22, textAlign: 'center', fontWeight: 700, color: BULK_ROW_STATUS[row.status]?.color || 'text.secondary', flexShrink: 0 }}>
                  {BULK_ROW_STATUS[row.status]?.label || '·'}
                </Typography>
              </Tooltip>
            ) : (
              <Checkbox size="small" checked={selected.has(i)} onChange={() => toggle(i)} disabled={disabled} sx={{ p: 0.25, mt: '-2px' }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{row.label}</Typography>
              {row.aenderungen.map((c) => (
                <Typography key={c.feld} sx={{ fontSize: '0.76rem', overflowWrap: 'anywhere' }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{c.feld}: </Box>
                  {/* Neuanlagen (alt == null) zeigen nur den Wert */}
                  {c.alt != null && (
                    <>
                      <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>{c.alt || '—'}</Box>
                      <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>→</Box>
                    </>
                  )}
                  <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>{c.neu || '—'}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      {p.begruendung && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider', fontStyle: 'italic' }}>
          {p.begruendung}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        {resolved ? (
          <>
            <Typography variant="caption" sx={{ color: (PROPOSAL_STATUS[p.status] || {}).color, fontWeight: 650 }}>
              {(PROPOSAL_STATUS[p.status] || {}).label}
            </Typography>
            {item.fehler && <Typography variant="caption" color="text.secondary">{item.fehler}</Typography>}
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {selected.size} von {p.aenderungen.length} ausgewählt
            </Typography>
            <Chip label={selected.size > 2 ? 'Übernehmen …' : 'Übernehmen'} size="small" clickable={!disabled} color="primary" sx={{ fontWeight: 650 }}
              disabled={disabled || selected.size === 0}
              onClick={() => onApply(p, [...selected].sort((a, b) => a - b))} />
            <Chip label="Verwerfen" size="small" clickable={!disabled} variant="outlined" disabled={disabled}
              onClick={() => onDismiss(p.id)} />
          </>
        )}
      </Box>
    </Box>
  );
}

const AssistantPanel = ({ open, onClose }) => {
  const { pathname } = useLocation();
  const [messages, setMessages] = useState([]); // {role, content, error?}
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Zweite Bestätigung für Massen-Aktionen: {art:'bulk',id,indices,anzahl} | {art:'alle',anzahl}
  const [confirm, setConfirm] = useState(null);
  // Datei-Anhang (A5): {upload_id, dateiname, zeilen} — wird mit der
  // nächsten Nachricht als sichtbarer Hinweis mitgeschickt
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pendingLoaded = useRef(false);

  // Offene Vorschläge aus früheren Gesprächen wiederherstellen — Karten
  // sollen durch ein Neuladen der Seite nie verloren gehen
  useEffect(() => {
    if (!open || pendingLoaded.current) return;
    pendingLoaded.current = true;
    axiosInstance.get('/assistant/proposals/offen/').then((r) => {
      const offene = r.data?.vorschlaege || [];
      if (!offene.length) return;
      setMessages((prev) => (prev.length ? prev : [
        { role: 'assistant', content: `Aus früheren Gesprächen ${offene.length === 1 ? 'ist noch ein Vorschlag' : `sind noch ${offene.length} Vorschläge`} offen — bestätige oder verwirf sie hier:` },
        ...offene.map((p) => ({ role: 'proposal', proposal: p })),
      ]));
    }).catch(() => {});
  }, [open]);
  const [toolActivity, setToolActivity] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, toolActivity, open]);

  const send = async (text) => {
    let question = (text ?? input).trim();
    if (!question || streaming) return;
    // Anhang als sichtbarer Hinweis anfügen — Kin liest die Datei über upload_lesen
    if (attachment) {
      question += `\n\n[Angehängte Datei: „${attachment.dateiname}“ — upload_id ${attachment.upload_id}, ${attachment.zeilen} Zeilen]`;
      setAttachment(null);
    }
    setInput('');
    // Verlauf für die API: nur Text-Turns — Vorschlags-Karten und Fehler bleiben lokal
    const history = [
      ...messages.filter((m) => !m.error && m.role !== 'proposal' && m.role !== 'chart'),
      { role: 'user', content: question },
    ];
    setMessages([...messages.filter((m) => !m.error), { role: 'user', content: question }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    setToolActivity(null);

    const appendToLast = (delta) => {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + delta };
        return next;
      });
    };
    const failWith = (message) => {
      setMessages((prev) => [...prev.slice(0, -1), { role: 'assistant', content: message, error: true }]);
    };

    try {
      const token = await ensureFreshToken();
      if (!token) {
        failWith('Deine Sitzung ist abgelaufen — bitte einmal neu anmelden.');
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      const response = await fetch(`${baseURL}/assistant/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: history.map(({ role, content }) => ({ role, content })),
          page_path: pathname,
        }),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        failWith(`Kin ist gerade nicht erreichbar (Fehler ${response.status}).`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // letzte (evtl. unvollständige) Zeile behalten
        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try { event = JSON.parse(line); } catch (e) { continue; }
          if (event.type === 'text') {
            setToolActivity(null);
            appendToLast(event.text);
          } else if (event.type === 'tool') {
            setToolActivity(TOOL_LABELS[event.name] || 'arbeitet …');
            // Absatz zwischen Text vor und nach einem Werkzeug-Lauf
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role !== 'assistant' || !last.content || last.content.endsWith('\n\n')) return prev;
              const next = [...prev];
              next[next.length - 1] = { ...last, content: last.content + '\n\n' };
              return next;
            });
          } else if (event.type === 'proposal') {
            // Karte vor der (weiter streamenden) Antwort-Blase einfügen
            setToolActivity(null);
            setMessages((prev) => {
              const next = [...prev];
              next.splice(next.length - 1, 0, { role: 'proposal', proposal: event.proposal });
              return next;
            });
          } else if (event.type === 'chart') {
            setToolActivity(null);
            setMessages((prev) => {
              const next = [...prev];
              next.splice(next.length - 1, 0, { role: 'chart', chart: event.chart });
              return next;
            });
          } else if (event.type === 'error') {
            failWith(event.message);
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        appendToLast('\n\n*(gestoppt)*');
      } else {
        failWith('Verbindung unterbrochen — bitte noch einmal versuchen.');
      }
    } finally {
      setStreaming(false);
      setToolActivity(null);
      abortRef.current = null;
      // Leere Antwort (z. B. Fehler vor dem ersten Token) nicht stehen lassen
      setMessages((prev) => prev.filter((m, i) => m.content !== '' || i !== prev.length - 1));
    }
  };

  const stop = () => abortRef.current?.abort();
  const reset = () => { if (!streaming) setMessages([]); };

  // Excel/CSV anhängen: sofort hochladen und parsen, Chip über der Eingabe
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axiosInstance.post('/assistant/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachment(r.data);
    } catch (err) {
      const msg = err.response?.data?.fehler || 'Upload fehlgeschlagen — bitte noch einmal versuchen.';
      setMessages((prev) => [...prev, { role: 'assistant', content: msg, error: true }]);
    } finally {
      setUploading(false);
    }
  };

  // Vorschlag anwenden/verwerfen — der Server prüft Rechte und Konflikte
  const proposalAction = async (id, action, extra = {}, opts = {}) => {
    const patch = (proposal, fehler) => setMessages((prev) => prev.map((m) => (
      m.role === 'proposal' && m.proposal.id === id
        ? { ...m, proposal: proposal || m.proposal, fehler }
        : m
    )));
    try {
      const r = await axiosInstance.post(`/assistant/proposals/${id}/action/`, { action, ...extra });
      patch(r.data.proposal, undefined);
      // Seite neu laden lassen, damit die Änderung sofort sichtbar ist
      if (action === 'apply' && !opts.silent) window.dispatchEvent(new CustomEvent('kin-applied'));
    } catch (e) {
      const data = e.response?.data;
      patch(data?.proposal, data?.fehler || 'Aktion fehlgeschlagen — bitte noch einmal versuchen.');
    }
  };

  const pendingIds = messages
    .filter((m) => m.role === 'proposal' && m.proposal.status === 'pending' && m.proposal.kind !== 'bulk_update')
    .map((m) => m.proposal.id);
  const applyAll = async () => {
    for (const id of pendingIds) await proposalAction(id, 'apply', {}, { silent: true }); // eslint-disable-line no-await-in-loop
    window.dispatchEvent(new CustomEvent('kin-applied'));
  };

  // Massen-Aktionen laufen über den Bestätigungs-Dialog (zweite Bestätigung)
  const confirmed = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.art === 'bulk') await proposalAction(c.id, 'apply', { indices: c.indices });
    else await applyAll();
  };

  // Beispiel angeklickt: Vorlagen mit [Platzhalter] ins Eingabefeld,
  // fertige Fragen direkt senden — beides zurück in die Chat-Ansicht.
  const applyExample = (text) => {
    setShowHelp(false);
    if (text.includes('[')) setInput(text);
    else send(text);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      // über der AppBar (die liegt auf drawer + 1)
      sx={{ zIndex: (t) => t.zIndex.drawer + 2, '& .MuiDrawer-paper': {
        width: { xs: '100%', sm: PANEL_WIDTH }, borderRadius: 0,
        // Theme-Drawer-Override (dunkle Nav-Leiste) neutralisieren: dieses
        // Panel ist helle/dunkle Inhaltsfläche mit normaler Textfarbe
        bgcolor: 'background.paper', color: 'text.primary',
        borderLeft: '1px solid', borderColor: 'divider',
        display: 'flex', flexDirection: 'column',
      } }}
    >
      {/* Kopf */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <KinAvatar size={30} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>Kin</Typography>
          <Typography variant="caption" color="text.secondary">KI-Assistent</Typography>
        </Box>
        <Tooltip title="Was kann Kin?">
          <IconButton size="small" onClick={() => setShowHelp((v) => !v)} aria-label="Beispiele anzeigen"
            sx={{ color: showHelp ? 'primary.main' : undefined }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Neues Gespräch">
          <span>
            <IconButton size="small" onClick={reset} disabled={streaming || messages.length === 0} aria-label="Neues Gespräch">
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="Schließen"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* „Was kann Kin?" — Beispiel-Übersicht */}
      {showHelp && (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <IconButton size="small" onClick={() => setShowHelp(false)} aria-label="Zurück zum Chat">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontWeight: 650 }}>Was kann Kin?</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ein Klick stellt die Frage direkt — Vorlagen mit [Platzhalter] landen zum Ausfüllen im Eingabefeld.
          </Typography>
          {HELP_SECTIONS.map((sec) => (
            <Box key={sec.title} sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {sec.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {sec.items.map((item) => (
                  <Box key={item} onClick={() => applyExample(item)} sx={(theme) => ({
                    px: 1.5, py: 1, borderRadius: '10px', fontSize: '0.85rem', lineHeight: 1.45,
                    bgcolor: theme.palette.tokens.surface2, cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                  })}>
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Bald möglich
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {HELP_UPCOMING.map((item) => (
              <Typography key={item} variant="body2" color="text.disabled" sx={{ fontSize: '0.85rem' }}>
                · {item}
              </Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Änderungen macht Kin nur als Vorschlag — du bestätigst jede Karte, bevor etwas gespeichert wird.
          </Typography>
        </Box>
      )}

      {/* Verlauf */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, display: showHelp ? 'none' : 'flex', flexDirection: 'column', gap: 1.25 }}>
        {messages.length === 0 && (
          <Box sx={{ my: 'auto', textAlign: 'center', px: 2 }}>
            <KinAvatar size={44} />
            <Typography sx={{ fontWeight: 650, mt: 1.5 }}>Hey, ich bin Kin.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ich kenne deine Leads, Projekte, Zeiten und den Teamkalender — frag mich einfach.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
              {SUGGESTIONS.map((s) => (
                <Chip key={s} label={s} clickable size="small" onClick={() => send(s)}
                  sx={{ height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
              ))}
            </Box>
            <Typography variant="body2" onClick={() => setShowHelp(true)}
              sx={{ mt: 1.5, color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              Alle Beispiele ansehen
            </Typography>
          </Box>
        )}

        {messages.map((m, i) => (m.role === 'chart' ? (
          <KinChart key={`c-${i}`} chart={m.chart} />
        ) : m.role === 'proposal' ? (
          m.proposal.kind.startsWith('bulk') ? (
            <BulkProposalCard key={`p-${m.proposal.id}`} item={m} disabled={streaming}
              onApply={(p, indices) => (indices.length > 2
                // Alexanders Regel: erst ab MEHR als 2 Datensätzen doppelt bestätigen
                ? setConfirm({ art: 'bulk', id: p.id, indices, anzahl: indices.length })
                : proposalAction(p.id, 'apply', { indices }))}
              onDismiss={(id) => proposalAction(id, 'dismiss')} />
          ) : (
            <ProposalCard key={`p-${m.proposal.id}`} item={m} onAction={proposalAction} disabled={streaming} />
          )
        ) : (
          <Box key={i} sx={{ display: 'flex', gap: 1, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {m.role === 'assistant' && <Box sx={{ flexShrink: 0, mt: 0.25 }}><KinAvatar size={22} /></Box>}
            <Box sx={(theme) => ({
              maxWidth: '85%', px: 1.5, py: 1, borderRadius: '12px', fontSize: '0.875rem', lineHeight: 1.55,
              overflowWrap: 'break-word',
              // Kin-Antworten rendern Markdown; Nutzer-/Fehlertexte bleiben roh
              whiteSpace: m.role === 'assistant' && !m.error ? 'normal' : 'pre-wrap',
              ...(m.role === 'user'
                ? { bgcolor: 'primary.main', color: 'primary.contrastText', borderBottomRightRadius: 4 }
                : m.error
                  ? { bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', borderBottomLeftRadius: 4 }
                  : { bgcolor: theme.palette.tokens.surface2, color: 'text.primary', borderBottomLeftRadius: 4, ...MD_SX }),
            })}>
              {m.role === 'assistant' && !m.error ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              ) : (
                m.content
              )}
              {m.role === 'assistant' && streaming && i === messages.length - 1 && !m.content && !toolActivity && (
                <CircularProgress size={14} sx={{ verticalAlign: 'middle' }} />
              )}
            </Box>
          </Box>
        )))}

        {toolActivity && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">Kin {toolActivity}</Typography>
          </Box>
        )}
      </Box>

      {/* Sammel-Aktion bei mehreren offenen Vorschlägen */}
      {!showHelp && !streaming && pendingIds.length >= 2 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {pendingIds.length} offene Vorschläge
          </Typography>
          <Chip label={pendingIds.length > 2 ? 'Alle übernehmen …' : 'Alle übernehmen'} size="small" clickable color="primary" sx={{ fontWeight: 650 }}
            onClick={() => (pendingIds.length > 2
              ? setConfirm({ art: 'alle', anzahl: pendingIds.length })
              : applyAll())} />
        </Box>
      )}

      {/* Anhang-Chip */}
      {attachment && !showHelp && (
        <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
          <Chip
            icon={<AttachFileIcon sx={{ fontSize: 15 }} />}
            label={`${attachment.dateiname} · ${attachment.zeilen} Zeilen`}
            size="small" onDelete={() => setAttachment(null)}
          />
        </Box>
      )}

      {/* Eingabe */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <input ref={fileInputRef} type="file" accept=".xlsx,.csv" hidden onChange={onFilePicked} />
        <Tooltip title="Excel/CSV anhängen">
          <span>
            <IconButton onClick={() => fileInputRef.current?.click()} disabled={streaming || uploading}
              sx={{ color: 'text.secondary' }} aria-label="Datei anhängen">
              {uploading ? <CircularProgress size={18} /> : <AttachFileIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth multiline maxRows={4} size="small"
          placeholder="Frag Kin …"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
        />
        {streaming ? (
          <Tooltip title="Stoppen">
            <IconButton onClick={stop} sx={{ color: 'error.main' }} aria-label="Antwort stoppen"><StopIcon /></IconButton>
          </Tooltip>
        ) : (
          <IconButton onClick={() => send()} disabled={!input.trim()} color="primary" aria-label="Senden">
            <SendIcon />
          </IconButton>
        )}
      </Box>

      {/* Zweite Bestätigung für Massen-Aktionen */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {confirm?.art === 'alle'
              ? `${confirm?.anzahl} offene Vorschläge anwenden?`
              : `${confirm?.anzahl} Datensätze ändern?`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirm?.art === 'alle'
              ? 'Alle offenen Vorschlags-Karten werden nacheinander angewendet.'
              : 'Die ausgewählten Zeilen der Massen-Änderung werden in die Datenbank geschrieben.'}
            {' '}Das ist die zweite Bestätigung — danach wird wirklich geschrieben.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={confirmed} autoFocus>
            Ja, {confirm?.anzahl} {confirm?.art === 'alle' ? 'Vorschläge' : 'Änderungen'} anwenden
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default AssistantPanel;
